import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { UserContext } from '../../../App'
import PersonalDetails from './form/PersonalDetails'
import BACS from './form/BACS'
import NotificationPreferences from './form/NotificationPreferences'
import CalendarFeeds from './form/CalendarFeeds'
import AppPreferences from './form/AppPreferences'
import API from '../../../services/API'

function TabPanel({ children, value, index }) {
  const isActive = value === index
  return (
    <Box
      role="tabpanel"
      hidden={!isActive}
      id={`account-tabpanel-${index}`}
      aria-labelledby={`account-tab-${index}`}
      sx={{ pt: 2 }}
    >
      {isActive ? children : null}
    </Box>
  )
}

// Tabs that save themselves and so hide the shared Save button
const SELF_SAVING_TABS = [3, 4] // Calendar, Preferences

const MyAccount = () => {
  const { user, setUser } = useContext(UserContext)
  const [accountData, setAccountData] = useState({})
  const [openedTab, setOpenedTab] = useState(0)

  const theme = useTheme()
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'))

  useEffect(() => {
    setAccountData({ ...user })
  }, [user])

  const handleTabChange = (event, newValue) => setOpenedTab(newValue)

  const buildAccountPayload = () => {
    const personnelInfo = accountData?.employee?.personnelInfo || {}
    const payroll = accountData?.employee?.payroll || {}

    return {
      prefix: accountData?.prefix ?? '',
      firstName: accountData?.firstName ?? '',
      lastName: accountData?.lastName ?? '',
      employee: {
        personnelInfo: {
          dob: personnelInfo.dob ?? null,
          nationalInsurance: personnelInfo.nationalInsurance ?? '',
          phoneNumber1: personnelInfo.phoneNumber1 ?? '',
          address: {
            lineOne: personnelInfo.address?.lineOne ?? '',
            lineTwo: personnelInfo.address?.lineTwo ?? '',
            town: personnelInfo.address?.town ?? '',
            city: personnelInfo.address?.city ?? '',
            county: personnelInfo.address?.county ?? '',
            postCode: personnelInfo.address?.postCode ?? '',
          },
          emergencyContact: {
            firstName: personnelInfo.emergencyContact?.firstName ?? '',
            lastName: personnelInfo.emergencyContact?.lastName ?? '',
            relationship: personnelInfo.emergencyContact?.relationship ?? '',
            phone1: personnelInfo.emergencyContact?.phone1 ?? '',
            phone2: personnelInfo.emergencyContact?.phone2 ?? '',
          },
          driving: {
            licenceNumber: personnelInfo.driving?.licenceNumber ?? '',
          },
        },
        payroll: {
          employmentType: payroll.employmentType ?? '',
          bacsName: payroll.bacsName ?? '',
          bacsAccountNumber: payroll.bacsAccountNumber ?? '',
          bacsSortCode: payroll.bacsSortCode ?? '',
          vatNumber: payroll.vatNumber ?? '',
        },
      },
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    API.put('/account/me', buildAccountPayload()).then((res) => {
      setUser(res.data)
      setAccountData(res.data)
    })
  }

  // Keeps labels short on mobile
  const tabs = useMemo(
    () => [
      { label: isSmDown ? 'Details' : 'Personal Details' },
      { label: 'BACS' },
      { label: 'Notifications' },
      { label: 'Calendar' },
      { label: 'Preferences' },
    ],
    [isSmDown],
  )

  return (
    <Box
      sx={{
        maxWidth: 900,
        mx: 'auto',
        px: { xs: 1.5, sm: 2 },
        pb: 2,
      }}
    >
      <Stack component="form" onSubmit={handleSubmit} spacing={2.5}>
        <Box sx={{ pt: { xs: 1, sm: 2 } }}>
          <Typography variant="h5" fontWeight={800}>
            My Account
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage your personal details, payment info, notification
            preferences, and shift calendar.
          </Typography>
        </Box>

        <Card
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          <CardHeader
            title={
              <Typography variant="subtitle1" fontWeight={800}>
                Account Settings
              </Typography>
            }
            sx={{
              py: 1.75,
              px: { xs: 2, sm: 3 },
            }}
          />

          <Divider />

          {/* Tabs header */}
          <Box
            sx={{
              px: { xs: 1, sm: 2 },
              // Uncomment to keep tabs visible while scrolling long forms:
              // position: { xs: 'sticky', sm: 'static' },
              // top: { xs: 0, sm: 'auto' },
              // zIndex: { xs: 2, sm: 'auto' },
              // bgcolor: 'background.paper',
            }}
          >
            <Tabs
              value={openedTab}
              onChange={handleTabChange}
              variant={isSmDown ? 'scrollable' : 'fullWidth'}
              scrollButtons={isSmDown ? 'auto' : false}
              allowScrollButtonsMobile
              sx={{
                minHeight: 44,
                '& .MuiTab-root': {
                  minHeight: 44,
                  textTransform: 'none',
                  fontWeight: 700,
                },
              }}
            >
              {tabs.map((t, idx) => (
                <Tab
                  key={t.label}
                  label={t.label}
                  id={`account-tab-${idx}`}
                  aria-controls={`account-tabpanel-${idx}`}
                />
              ))}
            </Tabs>
          </Box>

          <Divider />

          <CardContent sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
            <TabPanel value={openedTab} index={0}>
              <PersonalDetails
                accountData={accountData}
                setAccountData={setAccountData}
              />
            </TabPanel>

            <TabPanel value={openedTab} index={1}>
              <BACS accountData={accountData} setAccountData={setAccountData} />
            </TabPanel>

            <TabPanel value={openedTab} index={2}>
              <NotificationPreferences />
            </TabPanel>

            <TabPanel value={openedTab} index={3}>
              <CalendarFeeds />
            </TabPanel>

            <TabPanel value={openedTab} index={4}>
              <AppPreferences />
            </TabPanel>
          </CardContent>
        </Card>

        {/* Actions — the Calendar and Preferences tabs save themselves */}
        {!SELF_SAVING_TABS.includes(openedTab) && (
          <Box
            sx={{
              position: { xs: 'sticky', sm: 'static' },
              bottom: 0,
              zIndex: 1,
              bgcolor: { xs: 'background.default', sm: 'transparent' },
              pt: { xs: 1, sm: 0 },
              pb: { xs: 1, sm: 0 },
            }}
          >
            <Button
              variant="contained"
              fullWidth
              color="success"
              type="submit"
              size="large"
              sx={{ borderRadius: 3, py: 1.25, fontWeight: 800 }}
            >
              Save changes
            </Button>
          </Box>
        )}
      </Stack>
    </Box>
  )
}

export default MyAccount
